import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";

const normalize = (value) => String(value || "").trim().toLowerCase();

const defaultGetOptionValue = (option) => String(option?._id || option?.imei || option?.deviceId || "");

const defaultGetOptionLabel = (option) => {
  const name = option?.name || option?.vehicleName || option?.label || "Unnamed";
  const imei = option?.imei || option?.deviceId || option?._id || "-";
  const plate =
    option?.vehicleNumber ||
    option?.plateNumber ||
    option?.plateNo ||
    option?.registrationNo ||
    option?.registrationNumber ||
    "";
  return plate ? `${name} (${imei}) • ${plate}` : `${name} (${imei})`;
};

function buildSearchText(option) {
  return [
    option?.name,
    option?.vehicleName,
    option?.label,
    option?.imei,
    option?.deviceId,
    option?._id,
    option?.vehicleNumber,
    option?.plateNumber,
    option?.plateNo,
    option?.registrationNo,
    option?.registrationNumber,
  ]
    .map(normalize)
    .filter(Boolean)
    .join(" ");
}

export default function SearchableDeviceSelect({
  options = [],
  value,
  onChange,
  label = "Vehicle",
  placeholder = "Search by name, IMEI, or plate",
  disabled = false,
  multiple = false,
  clearable = true,
  size = "small",
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  helperText = "",
}) {
  const safeOptions = useMemo(
    () => (Array.isArray(options) ? options.filter(Boolean) : []),
    [options]
  );

  return (
    <Autocomplete
      multiple={multiple}
      options={safeOptions}
      value={value}
      onChange={(_, nextValue) => onChange?.(nextValue)}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      clearOnBlur={false}
      autoHighlight
      fullWidth
      isOptionEqualToValue={(option, selected) =>
        String(getOptionValue(option)) === String(getOptionValue(selected))
      }
      getOptionLabel={getOptionLabel}
      filterOptions={(items, state) => {
        const query = normalize(state.inputValue);
        if (!query) return items;
        return items.filter((item) => buildSearchText(item).includes(query));
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {multiple ? <Checkbox sx={{ mr: 1 }} checked={selected} /> : null}
          <Box sx={{ minWidth: 0, py: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: "normal" }}>
              {option?.name || option?.vehicleName || option?.label || "Unnamed"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, whiteSpace: "normal" }}>
              {option?.imei || option?.deviceId || option?._id || "-"}
              {option?.vehicleNumber ||
              option?.plateNumber ||
              option?.plateNo ||
              option?.registrationNo ||
              option?.registrationNumber
                ? ` • ${
                    option?.vehicleNumber ||
                    option?.plateNumber ||
                    option?.plateNo ||
                    option?.registrationNo ||
                    option?.registrationNumber
                  }`
                : ""}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          size={size}
        />
      )}
      clearOnEscape={clearable}
      ListboxProps={{
        style: {
          maxHeight: 320,
        },
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 360,
          },
        },
      }}
      sx={{
        minWidth: 320,
        "& .MuiAutocomplete-input": {
          minWidth: 0,
        },
      }}
    />
  );
}
